import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../common/Button'
import { Input } from '../common/Input'
import { Select } from '../common/Select'
import { useToast } from '../common/Toast'

interface SelectOption {
  value: string
  label: string
}

interface EditableFieldProps {
  label: string
  value: string
  placeholder?: string
  type?: 'text' | 'email' | 'tel' | 'select'
  options?: SelectOption[]
  required?: boolean
  validate?: (value: string) => string | null
  onSave?: (value: string) => Promise<{ success: boolean; error?: string }>
  disabled?: boolean
}

export function EditableField({
  label,
  value,
  placeholder,
  type = 'text',
  options,
  required = false,
  validate,
  onSave,
  disabled = false,
}: EditableFieldProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Update editValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value)
    }
  }, [value, isEditing])

  const handleEdit = () => {
    if (disabled) return
    setIsEditing(true)
    setEditValue(value)
    setError(null)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditValue(value)
    setError(null)
  }

  const handleSave = async () => {
    if (!onSave) return

    // Validate if required
    if (required && !editValue.trim()) {
      setError('This field is required')
      return
    }

    // Custom validation
    if (validate) {
      const validationError = validate(editValue)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    setIsSaving(true)
    setError(null)

    try {
      const result = await onSave(editValue)
      if (result.success) {
        setIsEditing(false)
        showToast({ message: t('settings.saved'), type: 'success' })
      } else {
        setError(result.error || t('settings.saveFailed'))
      }
    } catch {
      setError(t('settings.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && type !== 'select') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  }

  const rowStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap',
  }

  const labelContainerStyles: React.CSSProperties = {
    flex: '0 0 120px',
    paddingTop: '0.5rem',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  }

  const valueContainerStyles: React.CSSProperties = {
    flex: 1,
    minWidth: '200px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }

  const valueStyles: React.CSSProperties = {
    fontSize: '14px',
    color: value ? '#1a1a1a' : '#9ca3af',
    flex: 1,
    padding: '0.5rem 0',
  }

  const editContainerStyles: React.CSSProperties = {
    flex: 1,
    minWidth: '200px',
  }

  const buttonContainerStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
  }

  const editButtonStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#4F46E5',
    background: 'none',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0.25rem 0.5rem',
    opacity: disabled ? 0.5 : 1,
    minHeight: '44px',
    minWidth: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  // Get display value for select type
  const getDisplayValue = () => {
    if (type === 'select' && options) {
      const option = options.find(o => o.value === value)
      return option?.label || value || placeholder || 'Not set'
    }
    return value || placeholder || 'Not set'
  }

  if (isEditing) {
    return (
      <div style={containerStyles}>
        <div style={rowStyles}>
          <div style={labelContainerStyles}>
            <label style={labelStyles}>{label}</label>
          </div>
          <div style={editContainerStyles}>
            {type === 'select' && options ? (
              <Select
                value={editValue}
                onChange={setEditValue}
                options={options}
                placeholder={placeholder}
                error={error || undefined}
              />
            ) : (
              <Input
                type={type}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                placeholder={placeholder}
                error={error || undefined}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            )}
            <div style={buttonContainerStyles}>
              <Button onClick={handleSave} disabled={isSaving} size="small">
                {isSaving ? t('common.saving') : t('settings.save')}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={isSaving} size="small">
                {t('settings.cancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyles}>
      <div style={rowStyles}>
        <div style={labelContainerStyles}>
          <span style={labelStyles}>{label}</span>
        </div>
        <div style={valueContainerStyles}>
          <span style={valueStyles}>{getDisplayValue()}</span>
          <button
            style={editButtonStyles}
            onClick={handleEdit}
            disabled={disabled}
            aria-label={`Edit ${label}`}
          >
            {t('settings.edit')}
          </button>
        </div>
      </div>
    </div>
  )
}
